import type { IEmpresaComStatus } from './derivarProcessos';
import { statusEfetivo } from './derivarResumo';

/**
 * O PAINEL DO NÓ — a régua ditada pelo dono em 13/10:
 *
 * > *"eu tenho 209 empresas no simples nacional, dessas 115 deram ok e 94 erro,
 * > mas tem alguns casos que ele não vai dar o total de 209: vão ser empresas
 * > que ainda não apareceram no fluxo... quando eu clicar, elas precisam
 * > aparecer"*
 *
 * Toda empresa do UNIVERSO (as vigentes do regime da área) cai em exatamente UMA
 * categoria, por construção:
 *
 *     ok + erro + andamento + naoConstam + semMovimento === universo
 *
 * FONTE ÚNICA de desfecho: a lista do `/resumo` (último resultado vence — a
 * empresa que errou 3 vezes e depois passou é `ok`). O universo vem do
 * `/cobertura` e o movimento do `/movimento`; nenhum dos dois decide status.
 *
 * ALERTA É ERRO (decisão do dono, 13/10): `warn` soma em `erro`.
 *
 * PENDENTE NÃO É DESFECHO (dono, 13/10): o log de "pendente" (`skip`) não é ok
 * nem erro — *"passou mas ainda não pegou nada"* — e conta como não consta.
 *
 * SEM MOVIMENTO só tira do total quem NÃO apareceu aqui. A empresa sem
 * faturamento que o bot processou mesmo assim tem desfecho, e desfecho vence:
 * ela conta como ok/erro, não como "sem movimento".
 */
export type CategoriaPainel = 'ok' | 'erro' | 'andamento' | 'naoConstam' | 'semMovimento';

export const CATEGORIAS_PAINEL: CategoriaPainel[] = [
  'ok',
  'erro',
  'andamento',
  'naoConstam',
  'semMovimento',
];

/** As parcelas de UM lado do movimento (com ou sem). Somam `total`. */
export interface RecorteDeMovimento {
  total: number;
  ok: number;
  erro: number;
  andamento: number;
  /** Sem desfecho aqui: não constam (lado "com") ou sem movimento (lado "sem"). */
  ausentes: number;
}

export interface PainelDoNo {
  /** Empresas vigentes do regime — o total que as cinco categorias somam. */
  base: number;
  /**
   * `base − semMovimento`: as que faturaram ou já têm registro. INFORMATIVO —
   * a meta de 100% é sobre `base` (todas), ver `completo`.
   */
  exigidas: number;
  ok: string[];
  erro: string[];
  andamento: string[];
  /** No universo, com movimento (ou movimento desconhecido), sem registro aqui. */
  naoConstam: string[];
  /** No universo, sem faturamento na competência e sem registro aqui. */
  semMovimento: string[];
  /** Rodaram aqui mas não estão no universo (contrato encerrado, outro regime). */
  foraDoUniverso: string[];
  /**
   * PENDENTES (13/10, regra do dono): a empresa com log de "pendente" (`skip`)
   * *"passou mas ainda não pegou nada"* — não é ok nem erro, e conta como NÃO
   * CONSTA (ou sem movimento, se não faturou). Esta lista diz quais das ausentes
   * têm esse registro, para a tela poder mostrar o motivo.
   */
  pendentes: string[];
  /** Do universo, quantas faturaram na competência (informativo, não parcela). */
  comMovimentoNoUniverso: number | null;
  /**
   * OK e ERRO separados por quem FATUROU e quem não (13/10, pedido do dono: "o
   * com movimento e sem, eu preciso saber quantos % equivale de erro e ok em
   * cima daquela etapa"). `null` enquanto o movimento não respondeu.
   */
  porMovimento: { com: RecorteDeMovimento; sem: RecorteDeMovimento } | null;
  /** `false` enquanto o `/movimento` não respondeu: ninguém é "sem movimento". */
  movimentoConhecido: boolean;
  /** Verde só aqui: TODAS as empresas do total estão ok. */
  completo: boolean;
}

const ordenar = (a: string, b: string) => a.localeCompare(b, 'pt-BR', { numeric: true });

/**
 * Em qual categoria de DESFECHO um item da lista cai — ou `null` quando o
 * registro dele é só "pendente" (`skip`), que não é desfecho: ver `pendentes`.
 */
export const categoriaDoItem = (
  item: IEmpresaComStatus,
): Extract<CategoriaPainel, 'ok' | 'erro' | 'andamento'> | null => {
  const st = statusEfetivo(item);
  if (st === 'err' || st === 'warn') return 'erro';
  if (st === 'run' || st === 'wait') return 'andamento';
  if (st === 'skip') return null;
  return 'ok';
};

export const painelDoNo = (
  universo: string[],
  lista: IEmpresaComStatus[],
  comMovimento: Set<string> | null,
): PainelDoNo => {
  const noUniverso = new Set(universo);
  const ok: string[] = [];
  const erro: string[] = [];
  const andamento: string[] = [];
  const foraDoUniverso: string[] = [];
  const comRegistro = new Set<string>();
  const pendentes: string[] = [];

  lista.forEach(item => {
    if (!noUniverso.has(item.codiEmp)) {
      foraDoUniverso.push(item.codiEmp);
      return;
    }
    const categoria = categoriaDoItem(item);
    if (categoria === null) {
      // Pendente: fica FORA de `comRegistro` de propósito — ela cai no laço de
      // ausência logo abaixo, como "não consta" ou "sem movimento".
      pendentes.push(item.codiEmp);
      return;
    }
    comRegistro.add(item.codiEmp);
    if (categoria === 'erro') erro.push(item.codiEmp);
    else if (categoria === 'andamento') andamento.push(item.codiEmp);
    else ok.push(item.codiEmp);
  });

  const naoConstam: string[] = [];
  const semMovimento: string[] = [];
  noUniverso.forEach(codi => {
    if (comRegistro.has(codi)) return;
    if (comMovimento && !comMovimento.has(codi)) semMovimento.push(codi);
    else naoConstam.push(codi);
  });

  const base = noUniverso.size;
  const exigidas = base - semMovimento.length;
  const recorte = (fatura: boolean): RecorteDeMovimento => {
    const doLado = (codis: string[]) =>
      codis.filter(c => (comMovimento as Set<string>).has(c) === fatura).length;
    const partes = { ok: doLado(ok), erro: doLado(erro), andamento: doLado(andamento) };
    const ausentes = fatura ? naoConstam.length : semMovimento.length;
    return { ...partes, ausentes, total: partes.ok + partes.erro + partes.andamento + ausentes };
  };
  return {
    base,
    exigidas,
    ok: ok.sort(ordenar),
    erro: erro.sort(ordenar),
    andamento: andamento.sort(ordenar),
    naoConstam: naoConstam.sort(ordenar),
    semMovimento: semMovimento.sort(ordenar),
    foraDoUniverso: foraDoUniverso.sort(ordenar),
    pendentes: pendentes.sort(ordenar),
    comMovimentoNoUniverso: comMovimento
      ? universo.filter(codi => comMovimento.has(codi)).length
      : null,
    porMovimento: comMovimento ? { com: recorte(true), sem: recorte(false) } : null,
    movimentoConhecido: comMovimento !== null,
    // 100% de TODAS, com e sem movimento (dono, 13/10 — corrigindo a primeira
    // versão, que dispensava as sem movimento da meta).
    completo: base > 0 && ok.length === base,
  };
};

/** A identidade que o dono confere na calculadora. */
export const painelFecha = (p: PainelDoNo): boolean =>
  p.ok.length + p.erro.length + p.andamento.length + p.naoConstam.length + p.semMovimento.length ===
  p.base;

/**
 * Percentual INTEIRO que não mente nas pontas: 1 de 300 não vira "0%" e 299 de
 * 300 não vira "100%" — "100%" na tela tem que significar todas.
 */
export const pctDoPainel = (parte: number, total: number): number | null => {
  if (!total || total <= 0) return null;
  if (parte <= 0) return 0;
  if (parte >= total) return 100;
  return Math.min(99, Math.max(1, Math.round((parte / total) * 100)));
};

const FORMATO_PCT = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * "55,32%" — DUAS casas (dono, 14/10). As pontas não mentem: 1 de 30 mil não
 * vira "0,00%" e 29.999 de 30 mil não vira "100,00%".
 */
export const textoPct = (parte: number, total: number): string => {
  if (!total || total <= 0) return '—';
  if (parte <= 0) return `${FORMATO_PCT.format(0)}%`;
  if (parte >= total) return `${FORMATO_PCT.format(100)}%`;
  const pct = Math.min(99.99, Math.max(0.01, (parte / total) * 100));
  return `${FORMATO_PCT.format(pct)}%`;
};

/**
 * FUNIL ENTRE ETAPAS (13/10): das que deram OK na etapa anterior, quantas
 * apareceram nesta.
 *
 * > *"eu preciso saber se as 115 empresas que deram ok no fluxo 1 do RPA
 * > seguiram pro segundo fluxo e assim por diante"*
 *
 * "Seguiu" = tem QUALQUER registro aqui (ok, erro ou em andamento). Quem chegou
 * e falhou seguiu — o erro dela já está contado na categoria `erro` deste nó.
 */
export interface FunilDoNo {
  /** Nome da etapa (ou bot) anterior. */
  anterior: string;
  /** Quantas deram ok lá — a base do funil. */
  okAnterior: number;
  seguiram: string[];
  naoSeguiram: string[];
}

export const funilDoNo = (
  anterior: string,
  painelAnterior: PainelDoNo,
  painelAtual: PainelDoNo,
): FunilDoNo => {
  const aqui = new Set<string>([...painelAtual.ok, ...painelAtual.erro, ...painelAtual.andamento]);
  const seguiram: string[] = [];
  const naoSeguiram: string[] = [];
  painelAnterior.ok.forEach(codi => (aqui.has(codi) ? seguiram : naoSeguiram).push(codi));
  return { anterior, okAnterior: painelAnterior.ok.length, seguiram, naoSeguiram };
};

/** Faixas da escala de cor do círculo, em % de concluídas sobre o total. */
export const FAIXA_AMARELO = 40;
export const FAIXA_AZUL = 75;

/**
 * Tom do círculo — ESCALA de avanço (dono, 13/10: "vermelho quando tiver muitas
 * empresas com erro, aí amarelo, depois azul... até chegar no verde"):
 *
 *   cinza     ninguém rodou ainda
 *   vermelho  menos de 40% concluídas
 *   amarelo   de 40% a 74,99%
 *   azul      de 75% a 99,99%
 *   verde     100% — todas, com e sem movimento
 *
 * Depende SÓ do percentual escrito no círculo (14/10). A primeira versão também
 * pintava de vermelho quem tinha 30% ou mais em erro, e isso deixava um 60%
 * vermelho ao lado de um 45% amarelo — a cor contradizia o número.
 */
export const tomDoPainel = (p: PainelDoNo): 'ok' | 'err' | 'warn' | 'run' | 'idle' => {
  if (p.completo) return 'ok';
  if (!p.base || p.ok.length + p.erro.length + p.andamento.length === 0) return 'idle';
  const pctOk = (p.ok.length / p.base) * 100;
  if (pctOk < FAIXA_AMARELO) return 'err';
  if (pctOk < FAIXA_AZUL) return 'warn';
  return 'run';
};

/** Texto do círculo: ✓ quando completo, senão o percentual de ok sobre o TOTAL. */
export const circuloDoPainel = (p: PainelDoNo): string => {
  if (p.completo) return '✓';
  if (!p.base) return '—';
  return textoPct(p.ok.length, p.base);
};

/**
 * Competência do MOVIMENTO para um período da tela: o mês ANTERIOR ao início do
 * período. Em outubro os bots fecham setembro, então é o faturamento de setembro
 * que diz se a empresa tinha o que processar.
 */
export const competenciaDoMovimento = (de: string | undefined | null): string | null => {
  const m = /^(\d{4})-(\d{2})/.exec(de ?? '');
  if (!m) return null;
  const ano = Number(m[1]);
  const mes = Number(m[2]);
  if (mes < 1 || mes > 12) return null;
  const anoAnt = mes === 1 ? ano - 1 : ano;
  const mesAnt = mes === 1 ? 12 : mes - 1;
  return `${anoAnt}-${String(mesAnt).padStart(2, '0')}`;
};

/** "2026-09" → "09/2026". */
export const rotuloDaCompetencia = (competencia: string | null | undefined): string => {
  const m = /^(\d{4})-(\d{2})$/.exec(competencia ?? '');
  return m ? `${m[2]}/${m[1]}` : '';
};
